import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MdPhone, MdEmail, MdLocationOn, MdAccessTime, MdSend, MdWhatsapp } from 'react-icons/md';
import { FaLeaf } from 'react-icons/fa';
import { API_BASE_URL } from '../../config';
import './ContactUs.css';

gsap.registerPlugin(ScrollTrigger);

export default function ContactUs({ onBack, triggerNotification = () => { } }) {
  const containerRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cropType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const el = containerRef.current;

    // Hero text
    gsap.fromTo(el.querySelectorAll('.hero-fade'),
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: 'power3.out' }
    );

    // Info cards
    gsap.fromTo(el.querySelectorAll('.contact-info-card'),
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.7,
        stagger: 0.1,
        ease: 'back.out(1.2)',
        scrollTrigger: { trigger: el.querySelector('.info-row'), start: 'top 82%' }
      }
    );

    // Form panel
    gsap.fromTo(el.querySelector('.form-panel'),
      { x: -40, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.9,
        ease: 'power2.out',
        scrollTrigger: { trigger: el.querySelector('.main-grid'), start: 'top 80%' }
      }
    );

    // Right visual
    gsap.fromTo(el.querySelector('.contact-visual'),
      { x: 40, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.9,
        ease: 'power2.out',
        scrollTrigger: { trigger: el.querySelector('.main-grid'), start: 'top 80%' }
      }
    );

    return () => { ScrollTrigger.getAll().forEach(t => t.kill()); };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      triggerNotification('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/orders/contact-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        triggerNotification('Message sent! Our agri-experts will contact you within 24 hours.');
        setFormData({ name: '', email: '', phone: '', cropType: '', message: '' });
      } else {
        triggerNotification(data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      triggerNotification('Network error. Failed to connect to server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cropOptions = [
    'Rice / Paddy', 'Wheat', 'Cotton', 'Sugarcane', 'Maize / Corn',
    'Tomato', 'Onion', 'Banana', 'Groundnut', 'Soybean', 'Other'
  ];

  return (
    <div className="cu-root" ref={containerRef}>

      {/* ── HERO (split layout) ── */}
      <section className="cu-hero">
        {/* Left: content panel */}
        <div className="cu-hero-left">
          <span className="cu-eyebrow hero-fade"><FaLeaf /> Get In Touch</span>
          <h1 className="hero-fade">
            Reach Out to Our<br /><span>Agri-Experts</span>
          </h1>
          <p className="hero-fade">
            Whether you're a farmer, dealer, or institutional buyer — our team is here to guide you on the right organic solution for your crop and soil type.
          </p>

          {/* Quick-contact chips */}
          <div className="cu-hero-chips hero-fade">
            <a href="tel:+917228064418" className="cu-chip green-chip">
              <MdPhone size={18} /> +91 7228064418
            </a>
            <a href="mailto:orbifertorganic@gmail.com" className="cu-chip blue-chip">
              <MdEmail size={18} /> orbifertorganic@gmail.com
            </a>
          </div>

          {/* Trust badges */}
          <div className="cu-trust-row hero-fade">
            <div className="cu-trust-badge">
              <span className="ctb-num">24h</span>
              <span className="ctb-label">Response Time</span>
            </div>
            <div className="cu-trust-divider"></div>
            <div className="cu-trust-badge">
              <span className="ctb-num">20+</span>
              <span className="ctb-label">Years Expertise</span>
            </div>
            <div className="cu-trust-divider"></div>
            <div className="cu-trust-badge">
              <span className="ctb-num">9</span>
              <span className="ctb-label">Organic Products</span>
            </div>
          </div>
        </div>

        {/* Right: image panel */}
        <div className="cu-hero-right hero-fade">
          <div className="cu-hero-img-frame">
            <img src="/banner/farm-field-about.png" alt="Organic farming field" className="cu-hero-img" />
            <div className="cu-hero-img-badge">
              <span className="chib-dot"></span>
              <div>
                <strong>Orbifert Organic Pvt. Ltd.</strong>
                <span>Navsari, Gujarat · Est. 2015</span>
              </div>
            </div>
          </div>
          {/* Floating tag */}
          <div className="cu-float-tag">
            <MdLocationOn size={16} />
            <span>Visit us: Italva, Navsari – 396445</span>
          </div>
        </div>
      </section>

      {/* ── QUICK INFO CARDS ── */}
      <section className="info-row">
        <div className="cu-container">
          <div className="info-cards-row">
            <div className="contact-info-card">
              <div className="cic-icon phone-icon-bg">
                <MdPhone size={22} />
              </div>
              <div className="cic-content">
                <h4>Call &amp; WhatsApp</h4>
                <a href="tel:+917228064418" className="cic-primary">+91 7228064418</a>
                <p className="cic-sub">Customer Care · Mon–Sat 9am–6pm</p>
              </div>
            </div>

            <div className="contact-info-card">
              <div className="cic-icon email-icon-bg">
                <MdEmail size={22} />
              </div>
              <div className="cic-content">
                <h4>Email Us</h4>
                <a href="mailto:orbifertorganic@gmail.com" className="cic-primary">orbifertorganic@gmail.com</a>
                <p className="cic-sub">Response within 24 working hours</p>
              </div>
            </div>

            <div className="contact-info-card">
              <div className="cic-icon time-icon-bg">
                <MdAccessTime size={22} />
              </div>
              <div className="cic-content">
                <h4>Working Hours</h4>
                <p className="cic-primary">Mon–Sat: 9:00am – 6:00pm</p>
                <p className="cic-sub">Sunday: Closed</p>
              </div>
            </div>

            <div className="contact-info-card">
              <div className="cic-icon location-icon-bg">
                <MdLocationOn size={22} />
              </div>
              <div className="cic-content">
                <h4>Our Office</h4>
                <p className="cic-primary">Shop No-5 Neel Complex</p>
                <p className="cic-sub">Sisodara Rd., Italva, Navsari – 396445, Gujarat</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN GRID: FORM + VISUAL ── */}
      <section className="cu-main-section">
        <div className="cu-container">
          <div className="main-grid">

            {/* FORM PANEL */}
            <div className="form-panel">
              <div className="form-panel-head">
                <span className="cu-eyebrow-dark">Reach Out</span>
                <h2>Send us a Message</h2>
                <p>Fill in the form below. Our agricultural advisors typically respond within one business day.</p>
              </div>

              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-row-2">
                  <div className="form-group">
                    <label htmlFor="cf-name">Full Name *</label>
                    <input
                      id="cf-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g. Rajan Patel"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cf-email">Email Address *</label>
                    <input
                      id="cf-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label htmlFor="cf-phone">Phone / WhatsApp</label>
                    <input
                      id="cf-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 7228064418"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cf-crop">Crop Type</label>
                    <select
                      id="cf-crop"
                      name="cropType"
                      value={formData.cropType}
                      onChange={handleChange}
                    >
                      <option value="">Select your crop...</option>
                      {cropOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group full-w">
                  <label htmlFor="cf-message">Your Message *</label>
                  <textarea
                    id="cf-message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe your soil condition, crop issue, or product query..."
                    required
                  ></textarea>
                </div>

                <button type="submit" className="cu-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <><span className="cu-spinner"></span> Sending...</>
                  ) : (
                    <><MdSend size={18} /> Send Message</>
                  )}
                </button>
              </form>

              {/* WhatsApp quick CTA */}
              <div className="whatsapp-cta">
                <MdWhatsapp size={22} className="wa-icon" />
                <div>
                  <strong>Prefer WhatsApp?</strong>
                  <p>Chat directly with our farm advisors on <a href="https://wa.me/917228064418" target="_blank" rel="noopener noreferrer">+91 7228064418</a></p>
                </div>
              </div>
            </div>

            {/* VISUAL PANEL */}
            <div className="contact-visual">
              {/* Farm image with badge overlay */}
              <div className="cv-img-wrap">
                <img src="/banner/farmer-soil-about.png" alt="Indian farmer with organic soil" className="cv-img" />
                <div className="cv-badge">
                  <strong>Navsari, Gujarat</strong>
                  <span>Corporate headquarters &amp; formulation lab</span>
                </div>
              </div>

              {/* Stats strip */}
              <div className="cv-stats">
                <div className="cv-stat">
                  <span className="cv-num">20+</span>
                  <span className="cv-label">Years Experience</span>
                </div>
                <div className="cv-divider"></div>
                <div className="cv-stat">
                  <span className="cv-num">9</span>
                  <span className="cv-label">Organic Products</span>
                </div>
                <div className="cv-divider"></div>
                <div className="cv-stat">
                  <span className="cv-num">24h</span>
                  <span className="cv-label">Response Time</span>
                </div>
              </div>

              {/* Address card - Corporate Office */}
              <div className="cv-address-card">
                <MdLocationOn className="cv-addr-icon" size={22} />
                <div>
                  <strong>Orbifert Organic Pvt. Ltd. (Corporate Office)</strong>
                  <p>Shop No-5 Neel Complex, Sisodara Rd.<br />Italva, Navsari – 396445<br />Gujarat, India</p>
                  <a
                    href="https://maps.google.com/?q=Orbifert+Organic+Pvt.+Ltd.+Italva"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cv-maps-link"
                  >Open in Google Maps →</a>
                </div>
              </div>

              {/* Address card - Manufacturing Unit */}
              <div className="cv-address-card">
                <MdLocationOn className="cv-addr-icon" size={22} />
                <div>
                  <strong>Orbifert Organic Pvt. Ltd. (Manufacturing Unit)</strong>
                  <p>L-S No. 237-A2,<br />Vedchha-Mohanpur Ancheli Road,<br />Gandevi, Mohanpur,<br />District: Navsari, Gujarat – 396310</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=20.920965834090374,72.95396248122952"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cv-maps-link"
                  >Open in Google Maps →</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BOTTOM TAGLINE STRIP ── */}
      <section className="cu-tagline-strip">
        <div className="cu-container">
          <p>"Healthy Soil. Healthy Harvest. That's Orbifert's Promise to Every Farmer."</p>
        </div>
      </section>

    </div>
  );
}
